import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fonts,
  fontSizes,
  radii,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import { useContext } from "react";
import type { Components, ExtraProps } from "react-markdown";
import { MarkdownContext } from "./markdown-context";

type HastNode =
  | NonNullable<ExtraProps["node"]>
  | NonNullable<ExtraProps["node"]>["children"][number];

function codeText(node: HastNode): string {
  if (node.type === "text") return node.value;

  return "children" in node ? node.children.map(codeText).join("") : "";
}

export const markdownCodeElements = {
  pre: function MarkdownCodeBlock({ node }) {
    if (node === undefined)
      throw new Error("Markdown code blocks require a syntax node.");

    const message = useContext(MarkdownContext).variant === "message";
    return (
      <pre {...stylex.props(styles.codeBlock, message && styles.messageBlock)}>
        <code>{codeText(node).replace(/\n$/, "")}</code>
      </pre>
    );
  },
  code: ({ children }) => (
    <code {...stylex.props(styles.inlineCode)}>{children}</code>
  ),
} satisfies Components;

const styles = stylex.create({
  inlineCode: {
    fontFamily: fonts.mono,
    fontSize: fontSizes.small,
    backgroundColor: colors.codeSurface,
    paddingInline: spacing.space4,
    borderRadius: radii.small,
  },
  codeBlock: {
    fontFamily: fonts.mono,
    fontSize: fontSizes.small,
    lineHeight: 1.6,
    backgroundColor: colors.codeSurface,
    borderRadius: radii.medium,
    padding: spacing.space16,
    overflowX: "auto",
    whiteSpace: "pre",
    marginBlock: spacing.space20,
  },
  messageBlock: {
    marginBlock: 0,
    marginTop: { default: spacing.space8, ":first-child": 0 },
  },
});
